import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { conns } from '@cfg/axiosConfig.tsx';
import { logError } from '@tools/LogsHandler.tsx';
import { mapApiToShipping } from '@eshop/components/checkout/shippingMapper.ts';
import type { Address } from '@eshop/types/location.types.ts';
import type { ShippingMethod } from '@eshop/types/shipping.types.ts';

const sanitize = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : undefined;
};

const buildQueryKey = (address: Address): string => {
  const city =
    sanitize(address.verbose?.city) ??
    sanitize((address as Record<string, unknown>).city);
  const postal =
    sanitize((address as Record<string, unknown>).postal_code) ??
    sanitize((address as Record<string, unknown>).postcode) ??
    sanitize(address.postcode) ??
    sanitize(address.verbose?.postcode);

  return [city ?? '', postal ?? ''].join('|');
};

const buildQueryParams = (address: Address): Record<string, string> => {
  const params: Record<string, string> = {};
  const city =
    sanitize(address.verbose?.city) ??
    sanitize((address as Record<string, unknown>).city);
  const postal =
    sanitize((address as Record<string, unknown>).postal_code) ??
    sanitize((address as Record<string, unknown>).postcode) ??
    sanitize(address.postcode) ??
    sanitize(address.verbose?.postcode);

  if (city) params.city = city;
  if (postal) params.postal_code = postal;

  return params;
};

const sortShippingMethods = (methods: ShippingMethod[]): ShippingMethod[] => {
  return methods
    .slice()
    .sort((a, b) => {
      const costA = a.calculated_cost?.incl_tax ?? a.calculated_cost?.excl_tax;
      const costB = b.calculated_cost?.incl_tax ?? b.calculated_cost?.excl_tax;
      const normalizedA = Number.isFinite(Number(costA)) ? Number(costA) : Number.POSITIVE_INFINITY;
      const normalizedB = Number.isFinite(Number(costB)) ? Number(costB) : Number.POSITIVE_INFINITY;
      if (normalizedA === normalizedB) {
        return a.name.localeCompare(b.name);
      }
      return normalizedA - normalizedB;
    });
};

export interface UseShippingOptionsParams {
  enabled: boolean;
  address: Address | null | undefined;
}

export interface UseShippingOptionsResult {
  methods: ShippingMethod[];
  isLoading: boolean;
  error: string | null;
  defaultMethodId: number | null;
  refetch: () => Promise<void>;
  hasData: boolean;
}

export const useShippingOptions = ({ enabled, address }: UseShippingOptionsParams): UseShippingOptionsResult => {
  const [methods, setMethods] = useState<ShippingMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastQueryKeyRef = useRef<string | null>(null);
  const lastAddressRef = useRef<Address | null>(null);

  const reset = useCallback(() => {
    setMethods([]);
    setError(null);
    lastQueryKeyRef.current = null;
    lastAddressRef.current = null;
  }, []);

  const fetchMethods = useCallback(
    async (target: Address) => {
      try {
        setIsLoading(true);
        setError(null);
        const params = buildQueryParams(target);
        const response = await conns('eshop:shipping:active_list').get(params);
        const mapped: ShippingMethod[] = Array.isArray(response.data)
          ? response.data.map(mapApiToShipping)
          : [];
        setMethods(sortShippingMethods(mapped));
        lastAddressRef.current = target;
      } catch (err) {
        logError('useShippingOptions: failed to load methods', err);
        setError(
          'No pudimos cargar los métodos de envío. Revisá la dirección y volvé a intentarlo.',
        );
        setMethods([]);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }
    if (!address) {
      setMethods([]);
      setError(null);
      lastQueryKeyRef.current = null;
      return;
    }

    const queryKey = buildQueryKey(address);
    if (lastQueryKeyRef.current === queryKey) {
      return;
    }
    lastQueryKeyRef.current = queryKey;
    void fetchMethods(address);
  }, [address, enabled, fetchMethods, reset]);

  const refetch = useCallback(async () => {
    if (!enabled) {
      return;
    }
    if (address) {
      lastQueryKeyRef.current = buildQueryKey(address);
      await fetchMethods(address);
      return;
    }
    if (lastAddressRef.current) {
      await fetchMethods(lastAddressRef.current);
    }
  }, [address, enabled, fetchMethods]);

  const defaultMethodId = useMemo(() => {
    if (methods.length === 0) {
      return null;
    }
    const explicit = methods.find((method) => method.is_default);
    if (explicit) {
      return explicit.id;
    }
    return methods[0]?.id ?? null;
  }, [methods]);

  return {
    methods,
    isLoading,
    error,
    defaultMethodId,
    refetch,
    hasData: methods.length > 0,
  };
};

export default useShippingOptions;
